import { JSX, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Badge,
    Divider,
    IconButton,
    ListItemText,
    ListSubheader,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import {
    EMPTY_NOTIFICATIONS,
    NOTIFICATIONS_CHANGED,
    NotificationGroup,
    NotificationItem,
    Notifications,
    loadNotifications,
} from './notificationsApi';

/**
 * Things needing attention, on every screen.
 *
 * It exists because of a gap the dashboard cannot cover (user, 2026-09-08): both "needs attention"
 * cards filter on `status: 'new'`, so the moment somebody moves a request to Active Conversation it
 * drops off the dashboard and every later reply on it is invisible.
 *
 * **Written against sources rather than against sales conversations** (user, 2026-09-08), so the
 * bell can carry more later without a rewrite. It renders a title, a subtitle and a time, and
 * navigates to whatever route the source supplied; adding a kind of notification is a class in
 * HQ-API and no change here at all.
 */

/** Slow on purpose. None of this is a live chat, and it runs on every screen for every admin. */
const POLL_MS: number = 60000;

/** How long ago, in the shortest form that is still honest. */
const relative = (iso: string|null): string => {
    if (iso === null) {
        return '';
    }
    const then: number = new Date(iso).getTime();
    if (Number.isNaN(then)) {
        return '';
    }
    const minutes: number = Math.floor((Date.now() - then) / 60000);
    if (minutes < 1) {
        return 'just now';
    }
    if (minutes < 60) {
        return `${ minutes }m ago`;
    }
    const hours: number = Math.floor(minutes / 60);
    if (hours < 24) {
        return `${ hours }h ago`;
    }

    return `${ Math.floor(hours / 24) }d ago`;
};

export const NotificationBell = (): JSX.Element => {
    const navigate = useNavigate();
    const [anchor, setAnchor] = useState<HTMLElement|null>(null);
    const [notifications, setNotifications] = useState<Notifications>(EMPTY_NOTIFICATIONS);

    const poll = useCallback(async (): Promise<void> => {
        try {
            setNotifications(await loadNotifications());
        } catch {
            // A failed poll is not worth a toast on every screen every minute. The badge simply
            // does not change, and the next tick tries again.
        }
    }, []);

    useEffect((): (() => void) => {
        void poll();
        const timer = window.setInterval((): void => { void poll(); }, POLL_MS);
        // The timer is the floor, not the mechanism: anything that clears a notification says so
        // and the badge updates then, rather than up to a minute later.
        const onChanged = (): void => { void poll(); };
        window.addEventListener(NOTIFICATIONS_CHANGED, onChanged);

        return (): void => {
            window.clearInterval(timer);
            window.removeEventListener(NOTIFICATIONS_CHANGED, onChanged);
        };
    }, [poll]);

    const open = (item: NotificationItem): void => {
        setAnchor(null);
        if (item.route === null) {
            return;
        }
        // The screen it lands on fires NOTIFICATIONS_CHANGED once it has actually cleared the
        // item, so there is nothing to time here.
        navigate(item.route);
    };

    const { count, groups } = notifications;

    return (
        <>
            <Tooltip title={ count > 0 ? `${ count } needing attention` : 'Nothing needs attention' }>
                { /* Always rendered, never conditionally: a control that appears and disappears in
                     the header shifts everything beside it. */ }
                <IconButton
                    color="inherit"
                    aria-label={ `${ count } notifications` }
                    onClick={ (event): void => setAnchor(event.currentTarget) }
                >
                    <Badge badgeContent={ count } color="error">
                        <NotificationsIcon />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={ anchor }
                open={ anchor !== null }
                onClose={ (): void => setAnchor(null) }
                slotProps={ { paper: { sx: { minWidth: 340, maxWidth: 440 } } } }
            >
                { groups.length === 0 && (
                    <MenuItem disabled>
                        <Typography variant="body2" color="text.secondary">
                            { 'Nothing needs attention.' }
                        </Typography>
                    </MenuItem>
                ) }

                { groups.map((group: NotificationGroup, index: number): JSX.Element[] => [
                    index > 0
                        ? <Divider key={ `${ group.key }-divider` } />
                        : <span key={ `${ group.key }-nodivider` } />,
                    <ListSubheader
                        key={ `${ group.key }-header` }
                        sx={ {
                            bgcolor: 'transparent',
                            lineHeight: 2.2,
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            fontSize: 11,
                        } }
                    >
                        { `${ group.label } (${ group.count })` }
                    </ListSubheader>,
                    ...group.items.map((item: NotificationItem): JSX.Element => (
                        <MenuItem key={ item.id } onClick={ (): void => open(item) }>
                            <ListItemText
                                primary={ item.title }
                                secondary={
                                    relative(item.at)
                                        ? `${ item.subtitle } · ${ relative(item.at) }`
                                        : item.subtitle
                                }
                                slotProps={ {
                                    primary: { variant: 'body2' },
                                    secondary: { variant: 'caption' },
                                } }
                            />
                        </MenuItem>
                    )),
                ]).flat() }
            </Menu>
        </>
    );
};
